import Vue from 'vue'
import { detectedCulture } from '@/utilities/common/culture'
import { countryState } from 'utilities/common/countryState'
import { zipCodeRegexs, houseNumberRegexs } from 'utilities/common/validators'
import { find, toLower, startsWith } from 'lodash'

const state = () => ({
  countries: [],
  currencySymbolPosition: null,
  deliveryTime: null,
  contactFormState: null,
  contactTitles: [],
  paymentMethods: [],
  marketingOptIn: null,
  isDisplayTax: false,
  thankYouScript: null,
  extoleCoreTagUrl: null,
  genericHtml: null,
  genericScript: null,
  removeRefundBlock: false,
  countryProvinces: null
})

const getters = {
  paymentMethodsCreditCard (state) {
    return state.paymentMethods.filter(item => item.isCreditCard)
  },

  paymentMethodsCreditCardtemp (state) {
    return state.paymentMethods.filter(item => item.provider === 'Adyen' && item.isCreditCard) 
  },

  paymentMethodsCreditCardtempUK (state) {
    return state.paymentMethods.filter(item => item.provider === 'Worldpay' && item.isCreditCard) 
  },

  paymentMethodsNotCreditCard (state) {
    return state.paymentMethods.filter(item => !item.isCreditCard)
  }
}

const mutations = {
  setCultureConfig (state, cultureConfig) {
    state.countries = cultureConfig.countries
    state.currencySymbolPosition = cultureConfig.currencySymbolPosition
    state.deliveryTime = cultureConfig.deliveryTime
    state.contactFormState = cultureConfig.contactFormState
    state.contactTitles = cultureConfig.contactTitles
    state.marketingOptIn = cultureConfig.marketingOptIn
    state.isDisplayTax = cultureConfig.isDisplayTax
    state.thankYouScript = cultureConfig.thankYouScript
    state.extoleCoreTagUrl = cultureConfig.extoleCoreTagUrl
    state.genericHtml = cultureConfig.genericHtml
    state.genericScript = cultureConfig.genericScript
    state.removeRefundBlock = cultureConfig.removeRefundBlock
    state.countryProvinces = cultureConfig.countryProvinces
  },

  filterStates (state, { query, conuntryCode }) {
    let selectedCountry = find(state.countries, { code: conuntryCode })
    selectedCountry.states = selectedCountry.statesDuplicate.filter(state => startsWith(toLower(state.name), toLower(query)))
  },

  filterProvinces (state, { query }) {
    state.countryProvinces.provinces = state.countryProvinces.provincesDuplicate.filter(province => startsWith(toLower(province.name), toLower(query)))
  },

  filterCities (state, { query }) {
    state.countryProvinces.cities = state.countryProvinces.citiesDuplicate.filter(city => startsWith(toLower(city.name), toLower(query)))
  },

  setCities (state, query) {
    let cities = state.countryProvinces.provincesAndCities.find((item) => item.Code === query).Cities.map((item) => {
      return {
        code: item,
        name: item
      }
    })
    state.countryProvinces.cities = cities
    state.countryProvinces.citiesDuplicate = cities
  },

  filterBillingCities (state, { query }) {
    state.countryProvinces.billingCities = state.countryProvinces.billingCitiesDuplicate.filter(city => startsWith(toLower(city.name), toLower(query)))
  },

  setBillingCities (state, query) {
    let cities = state.countryProvinces.provincesAndCities.find((item) => item.Code === query).Cities.map((item) => {
      return {
        code: item,
        name: item
      }
    })
    state.countryProvinces.billingCities = cities
    state.countryProvinces.billingCitiesDuplicate = cities
  },

  setPaymentMethods (state, paymentMethods) {
    state.paymentMethods = paymentMethods
  }
}

const actions = {
  async fetchCultureConfig ({ dispatch, commit }) {
    try {
      dispatch('fetchPaymentMethods')

      const cultureConfig = await Vue.prototype.$prismic.client.query(
        Vue.prototype.$prismic.Predicates.at('document.type', 'culture'),
        { lang: detectedCulture.code }
      ).then((response) => {
        const data = response.results[0].data
        let countries = []
        if (process.env.VUE_APP_CONFIG === 'development' || process.env.VUE_APP_CONFIG === 'integration') {
          countries = data.countries_dev
        } else {
          countries = data.countries
        }
        countries.forEach(country => {
          country.states = countryState[country.code] || null
          country.statesDuplicate = countryState[country.code] || null
          country.zipCodeRegex = find(zipCodeRegexs, { 'code': country.code })['regex']
          country.houseNumberRegexs = find(houseNumberRegexs, { 'code': country.code })['regex']
          country.street_housenumber_extension_field = parseInt(country.street_housenumber_extension_field)
        })

        let countryProvinces = null

        if (data.provinces_and_cities) {
          const provincesAndCities = JSON.parse(data.provinces_and_cities)

          if (provincesAndCities != null) {
            const provinces = provincesAndCities.map((item) => {
              return {
                code: item.Code,
                name: item.Province
              }
            })

            countryProvinces = {
              provinces: provinces,
              provincesDuplicate: provinces,
              cities: [],
              citiesDuplicate: [],
              billingCities: [],
              billingCitiesDuplicate: [],
              provincesAndCities: provincesAndCities
            }
          }
        }

        return {
          countries,
          currencySymbolPosition: data.currency_symbol_position,
          deliveryTime: data.delivery_time,
          contactFormState: data.switch_contact_form,
          contactTitles: data.support_titles.map(item => item.text),
          marketingOptIn: data.marketing_opt_in,
          isDisplayTax: data.is_display_tax === 'yes',
          thankYouScript: data.thank_you_script,
          extoleCoreTagUrl: data.extole_core_tag_url,
          genericHtml: data.generic_html,
          genericScript: data.generic_script,
          removeRefundBlock: data.remove_refund_block === 'true',
          countryProvinces: countryProvinces
        }
      })

      commit('setCultureConfig', cultureConfig)
    } catch (error) {
      console.error(error)
    }
  },

  async fetchPaymentMethods ({ commit }) {
    const paymentMethods = await Vue.api.payment.getPaymentMethods()
    commit('setPaymentMethods', paymentMethods)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
